import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOPICS = [
  'accommodation','flights','destination','pricing','visa','health',
  'tips','food','activities','transfers','insurance','other',
];
const TOPIC_NL = {
  accommodation:'Accommodatie', flights:'Vluchten', destination:'Bestemming',
  pricing:'Prijzen', visa:'Visum', health:'Gezondheid', tips:'Tips',
  food:'Eten', activities:'Activiteiten', transfers:'Transfer',
  insurance:'Verzekering', other:'Overig',
};
const ANSWER_MODES = [
  { value: 'general_only',            label: 'Alleen algemeen' },
  { value: 'supplier_knowledge_only', label: 'Alleen leverancierskennis' },
  { value: 'hybrid',                  label: 'Hybride' },
];
const TABS = [
  { id: 'import',   label: 'URL Importeren' },
  { id: 'bronnen',  label: 'Bronnen' },
  { id: 'items',    label: 'Kennisitems' },
  { id: 'test',     label: 'Test Retrieval' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    success: 'bg-green-50 text-green-700 border-green-200',
    error:   'bg-red-50 text-red-700 border-red-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  const label = { success: 'Geslaagd', error: 'Fout', pending: 'Bezig' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${map[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {label[status] || status}
    </span>
  );
}

function ConfidenceBadge({ confidence }) {
  const map = {
    high:   'bg-green-50 text-green-700 border-green-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low:    'bg-gray-100 text-gray-600 border-gray-200',
    none:   'bg-gray-50 text-gray-400 border-gray-200',
  };
  const label = { high: 'Hoog', medium: 'Gemiddeld', low: 'Laag', none: 'Geen' };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${map[confidence] || map.none}`}>
      {label[confidence] || 'Onbekend'}
    </span>
  );
}

function Spinner() {
  return <div className="spinner spinner-blue" style={{ width: 18, height: 18, borderWidth: 2 }} />;
}

const SIGNAL_LABEL = {
  currency       : 'prijs',
  weight         : 'gewicht',
  dimension      : 'afmeting',
  surcharge      : 'toeslag',
  prebook_airport: 'vooraf/luchthaven',
  exception      : 'uitzondering',
  table          : 'tabel',
};

function ValidationCard({ validation }) {
  const [open, setOpen] = useState(false);
  const isOk = validation.status === 'ok';
  const isFailed = validation.status === 'failed';

  return (
    <div className={`pro-card p-4 border-l-4 ${
      isOk ? 'border-l-green-400' : isFailed ? 'border-l-red-400' : 'border-l-amber-400'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
            isOk
              ? 'bg-green-50 text-green-700 border-green-200'
              : isFailed
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
            {isOk ? 'Validatie OK' : isFailed ? 'Validatie: onvolledig' : 'Validatie: waarschuwing'}
          </span>
          <span className="text-xs text-gray-500">{validation.summary}</span>
        </div>
        {validation.warnings?.length > 0 && (
          <button onClick={() => setOpen(o => !o)} className="text-xs text-gray-400 hover:text-gray-700 flex-shrink-0">
            {open ? 'Verbergen' : `${validation.warnings.length} waarschuwing${validation.warnings.length !== 1 ? 'en' : ''}`}
          </button>
        )}
      </div>

      {open && validation.warnings?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {validation.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
              <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
              <span>
                <span className="font-medium text-gray-700">{w.message}</span>
                {w.signal_type && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">
                    {SIGNAL_LABEL[w.signal_type] || w.signal_type}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Import Tab ───────────────────────────────────────────────────────────────

function ImportTab({ authFetch }) {
  const [url, setUrl]           = useState('');
  const [supplier, setSupplier] = useState('');
  const [topic, setTopic]       = useState('');
  const [language, setLanguage] = useState('nl');
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');

  const handleImport = async () => {
    if (!url.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res  = await authFetch('/api/knowledge/import-url', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim(), supplier: supplier || undefined, topic: topic || undefined, language: language || undefined }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import mislukt');
      setResult(data);
      setUrl('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div className="p-6 max-w-2xl space-y-5">
      <div className="pro-card p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-gray-900">URL importeren</h2>
          <p className="text-xs text-gray-500 mt-0.5">Plak een webpagina-URL om de inhoud automatisch te extraheren en als kennisitems op te slaan.</p>
        </div>

        <div>
          <label className="label text-xs">URL *</label>
          <input
            className="input text-sm"
            placeholder="https://..."
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && handleImport()}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label text-xs">Leverancier</label>
            <input className="input text-sm" placeholder="bijv. TUI" value={supplier} onChange={e => setSupplier(e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Onderwerp</label>
            <select className="input text-sm" value={topic} onChange={e => setTopic(e.target.value)}>
              <option value="">Automatisch</option>
              {TOPICS.map(t => <option key={t} value={t}>{TOPIC_NL[t]}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Taal</label>
            <select className="input text-sm" value={language} onChange={e => setLanguage(e.target.value)}>
              <option value="nl">Nederlands</option>
              <option value="en">Engels</option>
              <option value="de">Duits</option>
              <option value="fr">Frans</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>
        )}

        <button
          onClick={handleImport}
          disabled={loading || !url.trim()}
          className="btn-primary text-sm flex items-center gap-2"
        >
          {loading ? <Spinner /> : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          )}
          {loading ? 'Importeren...' : 'Importeren'}
        </button>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="pro-card p-5 border-l-4" style={{ borderLeftColor: 'var(--brand)' }}>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-medium text-gray-900 text-sm">Import geslaagd</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3 truncate">{result.title}</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
              {[
                ['Tekens verwerkt', result.char_count?.toLocaleString('nl-NL')],
                ['Tekstblokken',    result.chunk_count],
                ['Nieuwe items',    result.items_inserted],
                ['Duplicaten',      result.items_duplicates],
                ['Blokfouten', result.chunks_errored],
                ['Duur',            `${result.duration_ms} ms`],
              ].filter(([, v]) => v !== undefined).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 py-0.5 border-b border-gray-50">
                  <span className="text-gray-500">{k}</span>
                  <span className={`font-medium ${k === 'Blokfouten' && v > 0 ? 'text-red-600' : 'text-gray-800'}`}>{v}</span>
                </div>
              ))}
            </div>
            {result.chunk_types && Object.keys(result.chunk_types).length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-1.5">Bloktypen gedetecteerd</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(result.chunk_types).map(([type, count]) => (
                    <span key={type} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        type === 'pricing_chunk'   ? 'bg-green-500'  :
                        type === 'table_chunk'     ? 'bg-blue-500'   :
                        type === 'exception_chunk' ? 'bg-amber-500'  :
                        type === 'faq_chunk'       ? 'bg-purple-400' : 'bg-gray-400'
                      }`} />
                      {type.replace('_chunk', '')} ({count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {result.extraction_errors?.length > 0 && (
            <div className="pro-card p-4 border-l-4 border-l-red-400">
              <p className="text-xs font-semibold text-red-700 mb-1.5">API-fout bij extractie</p>
              {result.extraction_errors.map((e, i) => (
                <p key={i} className="text-xs text-red-600 font-mono break-all">{e}</p>
              ))}
            </div>
          )}

          {result.validation && (
            <ValidationCard validation={result.validation} />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Bronnen Tab ──────────────────────────────────────────────────────────────

function BronnenTab({ authFetch }) {
  const [sources, setSources]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [reimporting, setReimp]   = useState(null);
  const [deleting, setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await authFetch('/api/knowledge/sources');
      const data = await res.json();
      setSources(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleReimport = async (source) => {
    setReimp(source.id);
    try {
      await authFetch('/api/knowledge/import-url', {
        method: 'POST',
        body: JSON.stringify({ url: source.url }),
      });
      await load();
    } catch (_) {}
    setReimp(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bron verwijderen? Bijbehorende tekstblokken worden ook verwijderd.')) return;
    setDeleting(id);
    try {
      await authFetch(`/api/knowledge/sources/${id}`, { method: 'DELETE' });
      setSources(prev => prev.filter(s => s.id !== id));
    } catch (_) {}
    setDeleting(null);
  };

  const filtered = sources.filter(s =>
    !search || s.title?.toLowerCase().includes(search.toLowerCase()) ||
    s.url?.toLowerCase().includes(search.toLowerCase()) ||
    s.domain?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <input
          className="input text-sm w-64"
          placeholder="Zoeken op URL of titel..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button onClick={load} className="btn-secondary text-sm flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Vernieuwen
        </button>
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} bron{filtered.length !== 1 ? 'nen' : ''}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">
            {sources.length === 0 ? 'Nog geen URL\'s geïmporteerd' : 'Geen resultaten'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
              <tr>
                {['Titel / URL', 'Status', 'Validatie', 'Blokken', 'Items', 'Geïmporteerd', ''].map(h => (
                  <th key={h} className="pro-table-header text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(src => (
                <tr key={src.id} className="pro-table-row group">
                  <td className="px-4 py-3 max-w-xs">
                    <p className="font-medium text-gray-900 truncate">{src.title || src.domain || '—'}</p>
                    <a href={src.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-500 hover:underline truncate block" title={src.url}>
                      {src.url.slice(0, 60)}{src.url.length > 60 ? '…' : ''}
                    </a>
                    {src.error_message && (
                      <p className="text-xs text-red-500 mt-0.5 truncate" title={src.error_message}>{src.error_message}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={src.fetch_status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {src.validation_status ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${
                          src.validation_status === 'ok'
                            ? 'bg-green-50 text-green-600 border-green-200'
                            : src.validation_status === 'failed'
                            ? 'bg-red-50 text-red-600 border-red-200'
                            : 'bg-amber-50 text-amber-600 border-amber-200'
                        }`}
                        title={src.possible_omissions > 0 ? `${src.possible_omissions} mogelijke omissie(s)` : 'Geen omissies'}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          src.validation_status === 'ok' ? 'bg-green-500' :
                          src.validation_status === 'failed' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        {src.validation_status === 'ok' ? 'OK' : src.possible_omissions > 0 ? `${src.possible_omissions} omissie${src.possible_omissions !== 1 ? 's' : ''}` : src.validation_status}
                      </span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{src.chunk_count ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{src.item_count ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {src.fetched_at ? new Date(src.fetched_at).toLocaleDateString('nl-NL') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleReimport(src)}
                        disabled={reimporting === src.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                        title="Opnieuw importeren"
                      >
                        {reimporting === src.id ? <Spinner /> : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                        Herimporten
                      </button>
                      <button
                        onClick={() => handleDelete(src.id)}
                        disabled={deleting === src.id}
                        className="inline-flex items-center px-2 py-1 rounded text-xs text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                        title="Verwijderen"
                      >
                        {deleting === src.id ? <Spinner /> : 'Verwijderen'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Items Tab ────────────────────────────────────────────────────────────────

const EMPTY_ITEM = { title:'', content:'', supplier:'', topic:'', language:'nl', tags:'', source_url:'', last_checked_at:'' };

function ItemsTab({ authFetch }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [fSupplier, setFSupplier] = useState('');
  const [fTopic, setFTopic]       = useState('');
  const [fLang, setFLang]         = useState('');
  const [fActive, setFActive]     = useState('');
  const [selected, setSelected]   = useState(null);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY_ITEM);
  const [saving, setSaving]       = useState(false);
  const [togglingId, setToggling] = useState(null);
  const [deletingId, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (fSupplier) p.set('supplier', fSupplier);
      if (fTopic)    p.set('topic', fTopic);
      if (fLang)     p.set('language', fLang);
      if (fActive)   p.set('is_active', fActive);
      const res  = await authFetch('/api/knowledge' + (p.toString() ? '?' + p : ''));
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoading(false);
  }, [authFetch, fSupplier, fTopic, fLang, fActive]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(item =>
    !search ||
    item.title?.toLowerCase().includes(search.toLowerCase()) ||
    item.content?.toLowerCase().includes(search.toLowerCase()) ||
    item.supplier?.toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || '', content: item.content || '',
      supplier: item.supplier || '', topic: item.topic || '',
      language: item.language || 'nl',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
      source_url: item.source_url || '',
      last_checked_at: item.last_checked_at ? item.last_checked_at.slice(0, 10) : '',
    });
    setSelected(null);
  };

  const handleSave = async () => {
    if (!form.title || !form.content) return;
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], last_checked_at: form.last_checked_at || null };
      const res = await authFetch(`/api/knowledge/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      if (res.ok) { setEditing(null); await load(); }
    } catch (_) {}
    setSaving(false);
  };

  const handleToggleActive = async (item) => {
    setToggling(item.id);
    try {
      await authFetch(`/api/knowledge/${item.id}`, { method: 'PUT', body: JSON.stringify({ ...item, is_active: !item.is_active }) });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_active: !i.is_active } : i));
      if (selected?.id === item.id) setSelected(s => ({ ...s, is_active: !s.is_active }));
    } catch (_) {}
    setToggling(null);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await authFetch(`/api/knowledge/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
      if (editing?.id === id) setEditing(null);
    } catch (_) {}
    setDeleting(null);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0 flex-wrap">
        <input className="input text-sm w-48" placeholder="Zoeken..." value={search} onChange={e => setSearch(e.target.value)} />
        <input className="input text-sm w-36" placeholder="Leverancier" value={fSupplier} onChange={e => setFSupplier(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} />
        <select className="input text-sm w-36" value={fTopic} onChange={e => setFTopic(e.target.value)}>
          <option value="">Alle onderwerpen</option>
          {TOPICS.map(t => <option key={t} value={t}>{TOPIC_NL[t]}</option>)}
        </select>
        <select className="input text-sm w-28" value={fLang} onChange={e => setFLang(e.target.value)}>
          <option value="">Alle talen</option>
          {['nl','en','de','fr'].map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select className="input text-sm w-28" value={fActive} onChange={e => setFActive(e.target.value)}>
          <option value="">Alles</option>
          <option value="true">Actief</option>
          <option value="false">Inactief</option>
        </select>
        <button onClick={load} className="btn-secondary text-sm">Filteren</button>
        {(fSupplier || fTopic || fLang || fActive) && (
          <button onClick={() => { setFSupplier(''); setFTopic(''); setFLang(''); setFActive(''); }} className="text-xs text-gray-400 hover:text-gray-700">Wissen</button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Content: list + detail pane */}
      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-24"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Geen items</div>
          ) : filtered.map(item => (
            <div
              key={item.id}
              className={`relative group border-b border-gray-100 ${!item.is_active ? 'opacity-50' : ''} ${selected?.id === item.id ? 'bg-blue-50 border-l-2' : ''}`}
              style={selected?.id === item.id ? { borderLeftColor: 'var(--brand)' } : {}}
            >
              <button onClick={() => { setSelected(item); setEditing(null); }} className="w-full text-left p-3.5 hover:bg-gray-50 transition-colors">
                <p className="font-medium text-gray-900 text-sm truncate">{item.title}</p>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {item.supplier && <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{item.supplier}</span>}
                  {item.topic    && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{TOPIC_NL[item.topic] || item.topic}</span>}
                  {item.language && <span className="text-xs text-gray-400">{item.language}</span>}
                </div>
              </button>
              <div className="absolute top-2.5 right-2 hidden group-hover:flex gap-0.5">
                <button onClick={() => handleToggleActive(item)} disabled={togglingId === item.id}
                  className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-amber-500 hover:bg-amber-50 transition-all"
                  title={item.is_active ? 'Deactiveren' : 'Activeren'}>
                  {togglingId === item.id ? <Spinner /> : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.is_active ? 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21' : 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'} />
                    </svg>
                  )}
                </button>
                <button onClick={() => openEdit(item)}
                  className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all" title="Bewerken">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button onClick={() => handleDelete(item.id)} disabled={deletingId === item.id}
                  className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all" title="Verwijderen">
                  {deletingId === item.id ? <Spinner /> : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Detail / Edit */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-5">
          {editing ? (
            <div className="pro-card p-5 max-w-2xl space-y-4">
              <h3 className="font-semibold text-gray-900 text-sm">Item bewerken</h3>
              <div>
                <label className="label text-xs">Titel *</label>
                <input className="input text-sm" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">Leverancier</label>
                  <input className="input text-sm" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} />
                </div>
                <div>
                  <label className="label text-xs">Onderwerp</label>
                  <select className="input text-sm" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}>
                    <option value="">—</option>
                    {TOPICS.map(t => <option key={t} value={t}>{TOPIC_NL[t]}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label text-xs">Inhoud *</label>
                <textarea className="input text-sm min-h-36 resize-y" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">Taal</label>
                  <input className="input text-sm" placeholder="nl" value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} />
                </div>
                <div>
                  <label className="label text-xs">Laatste controle</label>
                  <input type="date" className="input text-sm" value={form.last_checked_at} onChange={e => setForm(f => ({ ...f, last_checked_at: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label text-xs">Tags (kommagescheiden)</label>
                <input className="input text-sm" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
              </div>
              <div>
                <label className="label text-xs">Bron URL</label>
                <input className="input text-sm" value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} />
              </div>
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button onClick={handleSave} disabled={saving || !form.title || !form.content} className="btn-primary text-sm flex items-center gap-2">
                  {saving && <Spinner />}{saving ? 'Opslaan...' : 'Opslaan'}
                </button>
                <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Annuleren</button>
              </div>
            </div>
          ) : selected ? (
            <div className="pro-card p-5 max-w-2xl">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-900 text-sm leading-snug pr-4">{selected.title}</h3>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${selected.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {selected.is_active ? 'Actief' : 'Inactief'}
                  </span>
                  <button onClick={() => openEdit(selected)} className="btn-secondary text-xs">Bewerken</button>
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {selected.supplier && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{selected.supplier}</span>}
                {selected.topic    && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{TOPIC_NL[selected.topic] || selected.topic}</span>}
                {selected.language && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">{selected.language}</span>}
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-lg p-3.5 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mb-3">{selected.content}</div>

              {Array.isArray(selected.facts) && selected.facts.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Geëxtraheerde feiten</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {['Feit', 'Waarde', 'Eenheid', 'Conditie'].map(h => (
                            <th key={h} className="text-left px-3 py-1.5 font-medium text-gray-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selected.facts.map((f, i) => (
                          <tr key={i} className="border-b border-gray-100 last:border-0">
                            <td className="px-3 py-1.5 text-gray-700 font-medium">{f.label}</td>
                            <td className="px-3 py-1.5 text-gray-900 font-semibold">{f.value}</td>
                            <td className="px-3 py-1.5 text-gray-500">{f.unit || '—'}</td>
                            <td className="px-3 py-1.5 text-gray-400">{f.condition || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="space-y-1 text-xs text-gray-400">
                {selected.source_url && <p>Bron: <a href={selected.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selected.source_url}</a></p>}
                {selected.last_checked_at && <p>Laatste controle: {new Date(selected.last_checked_at).toLocaleDateString('nl-NL')}</p>}
                {(Array.isArray(selected.tags) ? selected.tags : []).filter(Boolean).length > 0 && <p>Tags: {selected.tags.join(', ')}</p>}
                <p>Aangemaakt: {new Date(selected.created_at).toLocaleDateString('nl-NL')}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <p className="text-xs text-gray-400">Selecteer een item om details te zien</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Test Retrieval Tab ───────────────────────────────────────────────────────

function TestTab({ authFetch }) {
  const [query, setQuery]       = useState('');
  const [supplier, setSupplier] = useState('');
  const [topic, setTopic]       = useState('');
  const [mode, setMode]         = useState('hybrid');
  const [limit, setLimit]       = useState(5);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState('');
  const [showDebug, setDebug]   = useState(false);

  const handleTest = async () => {
    if (!query.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res  = await authFetch('/api/knowledge/test-retrieval', {
        method: 'POST',
        body: JSON.stringify({ query: query.trim(), supplier: supplier || undefined, topic: topic || undefined, mode, limit }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Retrieval mislukt');
      setResult(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      <div className="pro-card p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-gray-900">Retrieval testen</h2>
          <p className="text-xs text-gray-500 mt-0.5">Test welke kennisitems worden opgehaald voor een bepaalde vraag.</p>
        </div>

        <div>
          <label className="label text-xs">Vraag *</label>
          <textarea
            className="input text-sm min-h-20 resize-none"
            placeholder="Bijv. Wat zijn de bagageregels bij TUI?"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label text-xs">Antwoordmodus</label>
            <select className="input text-sm" value={mode} onChange={e => setMode(e.target.value)}>
              {ANSWER_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Leverancier (optioneel)</label>
            <input className="input text-sm" placeholder="bijv. TUI" value={supplier} onChange={e => setSupplier(e.target.value)} />
          </div>
          <div>
            <label className="label text-xs">Onderwerp (optioneel)</label>
            <select className="input text-sm" value={topic} onChange={e => setTopic(e.target.value)}>
              <option value="">Alle</option>
              {TOPICS.map(t => <option key={t} value={t}>{TOPIC_NL[t]}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="label text-xs mb-0">Max. resultaten</label>
            <input type="number" className="input text-sm w-16 text-center" min={1} max={20} value={limit} onChange={e => setLimit(Number(e.target.value))} />
          </div>
          <button onClick={handleTest} disabled={loading || !query.trim()} className="btn-primary text-sm flex items-center gap-2">
            {loading ? <Spinner /> : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
            {loading ? 'Zoeken...' : 'Testen'}
          </button>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      </div>

      {result && (
        <div className="space-y-3">
          {/* Summary bar */}
          <div className="pro-card px-4 py-3 flex items-center gap-4 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">Confidence</span>
              <ConfidenceBadge confidence={result.confidence} />
            </div>
            <span className="text-gray-300">|</span>
            <span className="text-xs text-gray-500">{result.totalMatches} match{result.totalMatches !== 1 ? 'es' : ''}</span>
            <span className="text-gray-300">|</span>
            <span className="text-xs text-gray-500">Modus: <strong>{mode}</strong></span>
            <button onClick={() => setDebug(d => !d)} className="ml-auto text-xs text-gray-400 hover:text-gray-600">
              {showDebug ? 'Debug verbergen' : 'Debug tonen'}
            </button>
          </div>

          {/* Debug panel */}
          {showDebug && result.debug && (
            <div className="pro-card px-4 py-3 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 mb-2">Debug</p>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs">
                {[
                  ['Zoektermen', (result.debug.queryTerms || []).join(', ') || '—'],
                  ['Kandidaten', result.debug.candidateCount],
                  ['Score > 0', result.debug.scoredAboveZero],
                  ['Hoogste score', result.debug.topScore],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 py-0.5 border-b border-gray-100">
                    <span className="text-gray-500">{k}</span>
                    <span className="font-mono text-gray-700">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No results */}
          {result.items.length === 0 && (
            <div className="pro-card p-6 text-center text-gray-400 text-sm">
              Geen kennisitems gevonden voor deze vraag.
            </div>
          )}

          {/* Result items */}
          {result.items.map((item, i) => (
            <div key={item.id} className="pro-card p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: 'var(--brand)' }}>{i + 1}</span>
                  <h4 className="font-medium text-gray-900 text-sm">{item.title}</h4>
                </div>
                <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">
                  {item._score} pt
                </span>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed mb-3">{item.content}</p>

              <div className="flex flex-wrap gap-1.5 mb-2">
                {item.supplier && <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{item.supplier}</span>}
                {item.topic    && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{TOPIC_NL[item.topic] || item.topic}</span>}
                {item.language && <span className="text-xs text-gray-400 px-1.5 py-0.5">{item.language}</span>}
              </div>

              {/* Score breakdown */}
              {item._breakdown && (
                <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-100">
                  {Object.entries(item._breakdown)
                    .filter(([, v]) => typeof v === 'number' && v !== 0)
                    .map(([k, v]) => (
                      <span key={k} className={`text-xs px-1.5 py-0.5 rounded font-mono ${v > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                        {k}: {v > 0 ? '+' : ''}{v}
                      </span>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Knowledge() {
  const { authFetch }  = useAuth();
  const [tab, setTab]  = useState('import');

  return (
    <div className="flex flex-col h-full overflow-hidden page-enter" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Header */}
      <div className="pro-page-header">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Kennisbank</h1>
          <p className="text-gray-500 text-sm mt-0.5">Importeer, beheer en test leverancierskennis voor AI-antwoorden</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b border-gray-200 bg-white px-6 flex-shrink-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`pro-tab ${tab === t.id ? 'active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {tab === 'import'  && <div className="h-full overflow-y-auto"><ImportTab  authFetch={authFetch} /></div>}
        {tab === 'bronnen' && <BronnenTab authFetch={authFetch} />}
        {tab === 'items'   && <ItemsTab   authFetch={authFetch} />}
        {tab === 'test'    && <div className="h-full overflow-y-auto"><TestTab    authFetch={authFetch} /></div>}
      </div>
    </div>
  );
}
